import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EQUIPMENT_AND_MATERIAL_MODEL, STEPS_EQUIPMENT_AND_MATERIAL } from '../Model/Model';
import { PHOTO_PICKER_MODEL } from '../../../../../components/PhotoPicker/SubComponent/Models/Models';
import HandleChange from '../../../../../Helpers/HandleChange';
import defaultImage from '../../../../../assets/IconsTables/ic-tools.svg';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
    GetEquipmentDetailsById,
    UpdateEquipment,
} from '../../../../../reduxToolkit/EquipmentAndMaterial/EquipmentApi';
import { toast } from 'react-toastify';
import { errorMsg } from '../../../../../Apis/functions';
import { URL } from './../../../../../Apis/Constants';
import {
    createCategoryOrSubcategory,
    getCreateCategoryBody,
    getCreateSubcategoryBody,
    searchCategories,
    searchSubcategories,
    uploadEquipmentImage,
} from './CommonEquipmentMaterialApp';
/**
 * @Email : sgarcia@ibl.mx
 * @Author: Susana Garcia Rodriguez
 * @jobTittle: Frontend
 */
const UpdateEquipmentMaterialApp = () => {
    // Flag indicating whether the application is running in development mode
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    // Step labels used in the stepper component
    const [steps, setSteps] = useState([]);
    // Currently active step in the stepper
    const [activeStep, setActiveStep] = useState(0);
    // Flag used to indicate missing or invalid required field
    const [emptyFields, setEmptyFields] = useState(false);
    // Main equipment/material model state
    const [item, setItem] = useState({ ...EQUIPMENT_AND_MATERIAL_MODEL });
    // Generic change handler bound to the equipment model
    const { handleChange } = HandleChange({ model: item, setModel: setItem });
    // State that stores the selected image from the photo picker
    const [photoPickerFile, setPhotoPickerFile] = useState({ ...PHOTO_PICKER_MODEL, preview: defaultImage });
    // Controls whether the form inputs are disabled
    const [disabled, setDisabled] = useState();
    // Translation function
    const { t } = useTranslation();
    // Redux dispatcher
    const dispatch = useDispatch();
    // Options for dropdowns such as categories or subcategories
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [subCategoryOptions, setSubCategoryOptions] = useState([]);
    // Currently selected category
    const [selectedCategory, setSelectedCategory] = useState([]);
    // Currently selected subcategory
    const [selectedSubCategory, setSelectedSubCategory] = useState([]);
    // Contractor identifier retrieved from session storage
    const [contractorId, setContractorId] = useState(sessionStorage.getItem('contractorId'));
    // Equipment identifier obtained from route parameters
    const { id } = useParams();

    // Initializes step labels when translations change
    useEffect(() => {
        setSteps([
            t(STEPS_EQUIPMENT_AND_MATERIAL.GENERAL_DETAILS.TRANS),
            t(STEPS_EQUIPMENT_AND_MATERIAL.TECHNICAL_DETAILS.TRANS),
            t(STEPS_EQUIPMENT_AND_MATERIAL.PHOTO.TRANS),
        ]);
    }, [t]);
    // Fetches equipment details when the component is mounted
    useEffect(() => {
        getEquipmentById(id);
    }, []);
    // Retrieves equipment details by ID and initializes form state
    const getEquipmentById = (id) => {
        dispatch(GetEquipmentDetailsById(id)).then(
            ({
                payload: {
                    data: { data },
                },
            }) => {
                if (data?.id) {
                    setItem(EQUIPMENT_AND_MATERIAL_MODEL.set(data));
                    // Initialize selected category chip
                    if (data.categoryId) {
                        setSelectedCategory([
                            {
                                label: data.categoryName,
                                id: data.categoryId,
                            },
                        ]);
                    }
                    // Initialize selected subcategory chip
                    if (data.subCategoryId) {
                        setSelectedSubCategory([
                            {
                                label: data.subCategoryName,
                                id: data.subCategoryId,
                            },
                        ]);
                    }
                    if (data.fileUploadDto?.filePath) {
                        setPhotoPickerFile({
                            ...PHOTO_PICKER_MODEL,
                            preview: `${URL}${data.fileUploadDto.filePath}?t=${Date.now()}`,
                        });
                    } else {
                        setPhotoPickerFile({
                            ...PHOTO_PICKER_MODEL,
                            preview: defaultImage,
                        });
                    }
                }
            },
        );
    };

    // Extracts the selected chip ID or returns null if none is selected
    const getChipId = (arr) => arr?.[0]?.id || null;

    // Handles equipment update action
    const handleUpdate = async () => {
        // Validate required sections before updating
        const isValid = item.isValidGeneralDetails() && item.isValidTechnicalDetails();

        if (!isValid) {
            toast.warn(t('please_fill_all_fields'));
            setEmptyFields(true);

            return;
        }

        // Build the update request body
        const body = {
            ...item.toRegister({
                categoryId: getChipId(selectedCategory),
                subCategoryId: getChipId(selectedSubCategory),
            }),
            id: item.id,
        };

        // Dispatch the update action
        const { payload } = await dispatch(UpdateEquipment(body));
        const { data, code } = payload.data;

        if (data?.id) {
            toast.success(t('has_been_updated_successfully'));

            // If update successful, upload the new image using the same logic as in handleCreate
            if (photoPickerFile?.fileSelected || photoPickerFile?.selfieSrc) {
                await uploadEquipmentImage({
                    equipmentId: data.id,
                    fileSelected: photoPickerFile.fileSelected,
                    selfieSrc: photoPickerFile.selfieSrc,
                    dispatch,
                });
            }
            getEquipmentById(data.id);
        } else {
            errorMsg(code);
        }
    };

    // Updates the active step in the stepper
    const handleStepChange = (step) => {
        setActiveStep(step);
    };
    // Base request body used to create a category
    const createCategoryBody = getCreateCategoryBody(contractorId);
    // Base request body used to create a subcategory
    const createSubcategoryBody = getCreateSubcategoryBody(contractorId);
    // Creates a new category or subcategory and refreshes the list
    const handleCreateSubcategoryCategory = async (body) => {
        return await createCategoryOrSubcategory({
            body,
            contractorId,
            dispatch,
            onSuccess: () => toast.success(t('has_been_created_successfully')),
            onError: (code) => errorMsg(code),
        });
    };
    // Searches subcategories using a keyword
    const handleSearchSubcategory = async (key) => {
        return await searchSubcategories({
            key,
            contractorId,
            dispatch,
        });
    };
    // Searches categories using a keyword
    const handleSearchCategory = async (key) => {
        return await searchCategories({
            key,
            contractorId,
            dispatch,
        });
    };

    return {
        t,
        DEV,
        handleUpdate,
        steps,
        activeStep,
        setActiveStep,
        handleStepChange,
        item,
        disabled,
        handleChange,
        emptyFields,
        photoPickerFile,
        setPhotoPickerFile,
        id,
        categoryOptions,
        setCategoryOptions,
        subCategoryOptions,
        setSubCategoryOptions,
        selectedCategory,
        setSelectedCategory,
        selectedSubCategory,
        setSelectedSubCategory,
        dispatch,
        createSubcategoryBody,
        createCategoryBody,
        handleCreateSubcategoryCategory,
        handleSearchSubcategory,
        handleSearchCategory,
    };
};

export default UpdateEquipmentMaterialApp;
